/**
 * First-run Setup Wizard
 * Creates initial admin account when app is first launched
 * Phase 2: Multi-User Auth Foundation
 */

import React, { useState } from 'react';
import { createInitialAdmin } from '../../../platform/auth/authService';
import { validatePasswordStrength } from '../../../platform/auth/passwordUtils';
import { Ship, User, Lock, Check, AlertCircle } from 'lucide-react';

// Security: Setup wizard is disabled in production unless explicitly enabled
const SETUP_ENABLED = process.env.REACT_APP_ENABLE_SETUP === 'true';
const BOOTSTRAP_TOKEN = process.env.REACT_APP_BOOTSTRAP_TOKEN;

export const SetupWizard = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [bootstrapToken, setBootstrapToken] = useState('');
  const [tokenVerified, setTokenVerified] = useState(SETUP_ENABLED);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // If setup not enabled and no valid token, show blocked message
  if (!SETUP_ENABLED && !tokenVerified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">
            Setup Not Available
          </h2>
          <p className="text-slate-600 mb-6">
            This FleetSkipper instance requires administrator provisioning.
            Please contact FleetSkipper support to set up your organization.
          </p>
          <a
            href="mailto:info@fleetskipper.com"
            className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
          >
            Contact Support
          </a>

          {/* Hidden bootstrap token input for Jonathan */}
          <div className="mt-8 pt-4 border-t border-slate-200">
            <p className="text-xs text-slate-400 mb-2">Administrator Access</p>
            <input
              type="password"
              placeholder="Bootstrap token"
              value={bootstrapToken}
              onChange={(e) => setBootstrapToken(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg"
            />
            <button
              onClick={() => {
                if (bootstrapToken === BOOTSTRAP_TOKEN) {
                  setTokenVerified(true);
                }
              }}
              className="mt-2 text-xs text-slate-500 hover:text-cyan-600"
            >
              Verify
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    const strength = validatePasswordStrength(formData.password);
    if (!strength.valid) {
      newErrors.password = strength.errors.join('. ');
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep2()) return;

    setLoading(true);

    try {
      await createInitialAdmin(formData.name, formData.email, formData.password);

      // Ensure default vessel exists before proceeding
      // This runs the same migration that VesselProvider uses,
      // guaranteeing a vessel is created before the user logs in
      const { migrateToMultiVessel } = await import('../../../storage/migrations/migrateToMultiVessel');
      migrateToMultiVessel();

      onComplete?.();
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (!formData.password) return 'bg-gray-200';
    const strength = validatePasswordStrength(formData.password);
    if (strength.strength === 'strong') return 'bg-green-500';
    if (strength.strength === 'medium') return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getPasswordStrengthWidth = () => {
    if (!formData.password) return 'w-0';
    const strength = validatePasswordStrength(formData.password);
    if (strength.strength === 'strong') return 'w-full';
    if (strength.strength === 'medium') return 'w-2/3';
    return 'w-1/3';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full border border-gray-100">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-xl mb-4">
            <Ship className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome to FleetSkipper</h1>
          <p className="text-gray-600">Let's set up your administrator account</p>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center justify-center mb-8">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
            step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            {step > 1 ? <Check className="w-5 h-5" /> : <User className="w-5 h-5" />}
          </div>
          <div className={`w-20 h-1 transition-colors ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
            step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            <Lock className="w-5 h-5" />
          </div>
        </div>

        {/* Step 1: Admin Details */}
        {step === 1 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Administrator Details</h2>
            <p className="text-sm text-gray-600 mb-6">
              This account will have full access to manage the SMS system.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Captain John Smith"
                  autoFocus
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="admin@vessel.com"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            <button
              onClick={handleNext}
              className="w-full mt-6 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 2: Password */}
        {step === 2 && (
          <form onSubmit={handleSubmit}>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Create Password</h2>
            <p className="text-sm text-gray-600 mb-6">
              Choose a strong password to secure your account.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.password ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  autoFocus
                />

                {/* Password strength indicator */}
                <div className="mt-2">
                  <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full transition-all duration-300 ${getPasswordStrengthColor()} ${getPasswordStrengthWidth()}`} />
                  </div>
                </div>

                {errors.password && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.password}
                  </p>
                )}

                <div className="mt-3 text-xs text-gray-500 space-y-1">
                  <p className={formData.password.length >= 8 ? 'text-green-600' : ''}>
                    {formData.password.length >= 8 ? '✓' : '○'} At least 8 characters
                  </p>
                  <p className={/[A-Z]/.test(formData.password) ? 'text-green-600' : ''}>
                    {/[A-Z]/.test(formData.password) ? '✓' : '○'} One uppercase letter
                  </p>
                  <p className={/[a-z]/.test(formData.password) ? 'text-green-600' : ''}>
                    {/[a-z]/.test(formData.password) ? '✓' : '○'} One lowercase letter
                  </p>
                  <p className={/[0-9]/.test(formData.password) ? 'text-green-600' : ''}>
                    {/[0-9]/.test(formData.password) ? '✓' : '○'} One number
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            {errors.submit && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                {errors.submit}
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Account...' : 'Complete Setup'}
              </button>
            </div>
          </form>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-100">
          <p className="text-xs text-center text-gray-500">
            By creating an account, you agree to the FleetSkipper terms of service
            and privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
